/**
 * useCompanyCurrency Hook
 * 
 * Returns the default currency of the currently selected company.
 * This hook should be used throughout the app to ensure consistent
 * currency formatting based on the company's settings.
 * 
 * @returns Currency code (e.g., 'BRL', 'USD', 'EUR')
 * 
 * @example
 * ```tsx
 * const currency = useCompanyCurrency();
 * const formatted = formatCurrency(1234.56, currency); // "€ 1.234,56" for EUR
 * ```
 */

import { useCompanyContext } from '../contexts/CompanyContext';
import { DEFAULT_CURRENCY } from '../lib/constants/currencies';
import type { Currency } from '../types/currency';

export function useCompanyCurrency(): Currency {
  const { selectedCompanies } = useCompanyContext();
  
  // Use the first selected company's currency (or default if none)
  const currency = selectedCompanies[0]?.default_currency || DEFAULT_CURRENCY;
  
  return currency as Currency;
}

